import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { nutrientService } from '../utils/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

/**
 * Hook to fetch nutrients for a school with React Query
 */
export const useNutrients = (schoolId, options = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  // Force cache invalidation on mount
  useEffect(() => {
    if (schoolId) {
      // Invalidate the query on mount to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ['nutrients', schoolId]
      });
    }
  }, [schoolId, queryClient]);
  
  return useQuery({
    queryKey: ['nutrients', schoolId],
    queryFn: () => nutrientService.listNutrients(schoolId),
    enabled: !!schoolId,
    // Disable caching in the browser
    cacheTime: 0,
    // Always refetch on window focus
    refetchOnWindowFocus: true,
    // Don't use stale data
    staleTime: 0,
    onError: (error) => {
      console.error('Error fetching nutrients:', error);
      toast.error(t('errors.fetchNutrientsFailed'));
    },
    ...options
  });
};

/**
 * Hook to fetch only enabled nutrients for a school
 */
export const useEnabledNutrients = (schoolId, language, options = {}) => {
  const { t } = useTranslation();
  
  return useQuery({
    queryKey: ['nutrients', schoolId, { enabled: true }],
    queryFn: () => nutrientService.listEnabledNutrients(schoolId, language),
    enabled: !!schoolId && !!language,
    onError: (error) => {
      console.error('Error fetching enabled nutrients:', error);
      toast.error(t('errors.fetchNutrientsFailed'));
    },
    ...options
  });
};

/**
 * Hook to create a new nutrient
 */
export const useCreateNutrient = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ nutrient, schoolId }) => 
      nutrientService.createNutrient(nutrient, schoolId),
    onSuccess: (_, { schoolId }) => {
      queryClient.invalidateQueries({
        queryKey: ['nutrients', schoolId]
      });
      toast.success(t('settings.nutrientCreated'));
    },
    onError: (error) => {
      console.error('Error creating nutrient:', error);
      toast.error(t('errors.createNutrientFailed'));
    }
  });
};

/**
 * Hook to update an existing nutrient
 */
export const useUpdateNutrient = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ nutrientId, nutrient, schoolId }) => 
      nutrientService.updateNutrient(nutrientId, nutrient, schoolId),
    onSuccess: (_, { schoolId }) => {
      queryClient.invalidateQueries({
        queryKey: ['nutrients', schoolId]
      });
      toast.success(t('settings.nutrientUpdated'));
    },
    onError: (error) => {
      console.error('Error updating nutrient:', error);
      toast.error(t('errors.updateNutrientFailed'));
    }
  });
};

/**
 * Hook to delete a nutrient
 */
export const useDeleteNutrient = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ nutrientId, schoolId }) => 
      nutrientService.deleteNutrient(nutrientId, schoolId),
    onSuccess: (_, { schoolId }) => {
      queryClient.invalidateQueries({
        queryKey: ['nutrients', schoolId]
      });
      toast.success(t('settings.nutrientDeleted'));
    },
    onError: (error) => {
      console.error('Error deleting nutrient:', error);
      toast.error(t('errors.deleteNutrientFailed'));
    }
  });
}; 